import {
	ApprovalTimeEntryStatus,
	PageInfo,
	ProjectTimeApproval,
	useAcceptTimeApprovalMutation,
	useGetAllTimeApprovalsLazyQuery,
	useRejectTimeApprovalMutation,
} from "@repo/server-api";
import { useNotification } from '@repo/shared/components';
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { DataSource } from "@repo/legacy/components/shared/lists/DataSource";
import { PaginationModel } from "@repo/legacy/components/shared/lists/PaginationModel";


export function useAllTimeApprovalsDataSource(): DataSource<ProjectTimeApproval> {
	const [rows, setRows] = useState<ProjectTimeApproval[]>([]);
	const [rowCount, setRowCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [, setPageInfo] = useState<PageInfo | null>(null);
	const [startDate, setStartDate] = useState<string>("");
	const [endDate, setEndDate] = useState<string>("");
	const [searchBy, setSearchBy] = useState<string>("");
	const [filterByProjectId, setFilterByProjectId] = useState<string | null>(null);
	const [filterByEmployeeId, setFilterByEmployeeId] = useState<string | null>(null);
	const [filterByStatuses, setFilterByStatuses] = useState<ApprovalTimeEntryStatus[] | null>(null);
	const [pageModal, setPageModal] = useState<PaginationModel>({
		page: 0,
		pageSize: 10,
	});
	const { t } = useTranslation();

	const { showNotification } = useNotification();

	const [rejectTimeApproval] = useRejectTimeApprovalMutation({
		fetchPolicy: "no-cache",
		onCompleted(data) {
			if (data && !data.rejectTimeApproval.errors) {
				showNotification(t("projects.timeSheets.rejectSuccess"), "success");
				refresh();
			} else if (data && data.rejectTimeApproval.errors) {
				data.rejectTimeApproval.errors.forEach((error) => {
					showNotification(error.message, "error");
				});
			}
		},
		onError(error) {
			showNotification(error.message, "error");
		},
	});

	const [acceptTimeApproval] = useAcceptTimeApprovalMutation({
		fetchPolicy: "no-cache",
		onCompleted(data) {
			if (data && !data.acceptTimeApproval.errors) {
				showNotification(t("projects.timeSheets.approveSuccess"), "success");
				refresh();
			} else if (data && data.acceptTimeApproval.errors) {
				data.acceptTimeApproval.errors.forEach((error) => {
					showNotification(error.message, "error");
				});
			}
		},
		onError(error) {
			showNotification(error.message, "error");
		},
	});

	const [getTimeApprovals] = useGetAllTimeApprovalsLazyQuery({
		fetchPolicy: "no-cache",
		onCompleted(data) {
			if (data && data.allTimeApprovals) {
				setRows(data.allTimeApprovals.nodes as ProjectTimeApproval[]);
				setRowCount(data.allTimeApprovals.totalCount);
				setPageInfo(data.allTimeApprovals.pageInfo);
				setIsLoading(false);
			}
		},
		onError() {
			setIsLoading(false);
		},
	});

	const refresh = () => {
		fetchDataStartEnd(
			pageModal,
			startDate,
			endDate,
			searchBy,
			filterByProjectId ?? null,
			filterByEmployeeId ?? null,
			filterByStatuses && filterByStatuses.length > 0 ? filterByStatuses : null,
		);
	};

	const fetchDataStartEnd = (
		page: PaginationModel,
		start: string,
		end: string,
		s: string = "",
		projectId?: string | null,
		employeeId?: string | null,
		statuses?: ApprovalTimeEntryStatus[] | null,
	) => {
		setIsLoading(true);
		setPageModal(page);
		setStartDate(start);
		setEndDate(end);
		setSearchBy(s);
		setFilterByProjectId(projectId || null);
		setFilterByEmployeeId(employeeId || null);
		setFilterByStatuses(statuses && statuses.length > 0 ? statuses : null);
		getTimeApprovals({
			variables: {
				after: (page.pageSize * page.page).toString(),
				before: null,
				end: dayjs(end).endOf("year").format("YYYY-MM-DD"),
				first: page.pageSize,
				last: null,
				start: dayjs(start).startOf("year").format("YYYY-MM-DD"),
				search: s,
				projectId: projectId,
				employeeId: employeeId,
				statuses: statuses && statuses.length > 0 ? statuses : undefined,
			},
		})
			.then((response) => {
				if (response.data && response.data.allTimeApprovals) {
					setRows(response.data.allTimeApprovals.nodes as ProjectTimeApproval[]);
					setRowCount(response.data.allTimeApprovals.totalCount);
					setPageInfo(response.data.allTimeApprovals.pageInfo);
					setIsLoading(false);
				}
			})
			.catch(() => {
				setIsLoading(false);
			});
	};

	const rejectDataWithReason = (id: string, reason: string) => {
		rejectTimeApproval({
			variables: {
				projectTimeApprovalId: id,
				description: reason,
			},
		});
	};

	const approveTimeApproval = (id: string) => {
		acceptTimeApproval({
			variables: {
				projectTimeApprovalId: id,
			},
		});
	};

	return {
		rows,
		rowCount,
		isLoading,
		refresh,
		fetchDataStartEnd,
		rejectDataWithReason,
		approveTimeApproval,
	};
}
